import { IMAGE_FIELD_MAX_URLS } from './custom-field-image'

/**
 * Bedroom showcase photo-caption matching (dashboard#1707, port of the
 * toolkit's `BedroomShowcase\Captions`, toolkit 3.246.0 / 3.247.0).
 *
 * Operators caption listing photos by room in the PMS ("Primary Bedroom 3rd
 * Floor - King Bed", "Bedroom 4 - 2 Twins Over 2 Full Beds"). A bedroom whose
 * photo custom field is empty takes every listing photo whose caption names
 * the room, and each photo keeps its full caption.
 *
 * Matching is deliberately literal: the room name must appear in the caption
 * as whole words, compared case- and punctuation-insensitively. "Bedroom 1"
 * finds "Bedroom #1 - Queen" and "bedroom1 king", never "Bedroom 10" or
 * "Bedroom 1st floor". A room's ensuite rides along: a caption that names the
 * room with "Bedroom" swapped for "Bathroom", "Bath", "Ensuite" or "En suite"
 * joins that bedroom. A bare "Bedroom" room has no ensuite spelling. When
 * several room names fit one caption the longest wins (an earlier room on a
 * tie), and a photo lands in one room only.
 */

/** Words that stand in for "bedroom" when a caption names the ensuite. */
const ENSUITE_WORDS = ['bathroom', 'bath', 'ensuite', 'en suite'] as const

export type CaptionPhoto = { url: string; caption: string }

/**
 * Lowercase; every run of punctuation or whitespace collapses to one space; a
 * space is forced between letters and a trailing digit run so "Bedroom2" and
 * "Bedroom 2" agree. Ordinals stay whole ("2nd" is not "2 nd"), otherwise
 * "Bedroom 2nd Floor" would read as Bedroom 2. (Lookbehind-free on purpose:
 * Safari before 16.4 cannot parse it, see amenities-icon-resolver.test.ts.)
 */
export function normalizeCaption(text: string): string {
  return text
    .toLowerCase()
    .replace(/(\p{L})(?=\p{N}+(?![\p{N}\p{L}]))/gu, '$1 ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

/** A normalized room name plus its ensuite spellings, the room name first. */
function needles(room: string): string[] {
  const padded = ` ${room} `
  if (room === 'bedroom' || !padded.includes(' bedroom ')) return [room]
  return [room, ...ENSUITE_WORDS.map((word) => padded.replace(' bedroom ', ` ${word} `).trim())]
}

function namesRoom(paddedHaystack: string, room: string): boolean {
  return needles(room).some((needle) => paddedHaystack.includes(` ${needle} `))
}

/** Whether a caption names a room (or its ensuite). */
export function captionMatchesRoom(caption: string, roomName: string): boolean {
  const room = normalizeCaption(roomName)
  if (room === '') return false
  return namesRoom(` ${normalizeCaption(caption)} `, room)
}

/**
 * Distribute listing photos across rooms by caption. Every room takes part in
 * the matching (a photo captioned for a room that already has hand-picked
 * photos is claimed by that room, not spilled onto a neighbour); the caller
 * decides which rooms to fill.
 *
 * @param photos Listing photos in gallery order.
 * @param names  Room names keyed by room position.
 * @returns Room position -> its photos, in gallery order, distinct by URL, at
 *          most IMAGE_FIELD_MAX_URLS (the toolkit's Images::MAX) each. Rooms
 *          that matched nothing are absent.
 */
export function assignCaptionPhotos(
  photos: ReadonlyArray<Partial<CaptionPhoto> | null | undefined>,
  names: ReadonlyArray<string | null | undefined>,
): Map<number, CaptionPhoto[]> {
  const rooms: Array<[number, string]> = []
  names.forEach((name, position) => {
    const normalized = normalizeCaption(String(name ?? ''))
    if (normalized !== '') rooms.push([position, normalized])
  })

  const assigned = new Map<number, CaptionPhoto[]>()
  if (rooms.length === 0) return assigned

  for (const photo of photos) {
    if (!photo || typeof photo !== 'object') continue
    const url = String(photo.url ?? '').trim()
    const caption = String(photo.caption ?? '').trim()
    if (url === '' || caption === '') continue

    const haystack = ` ${normalizeCaption(caption)} `
    let best: number | null = null
    let bestLen = 0
    // Longest room name wins; on a tie the earlier room.
    for (const [position, room] of rooms) {
      if (room.length > bestLen && namesRoom(haystack, room)) {
        best = position
        bestLen = room.length
      }
    }
    if (best === null) continue

    const list = assigned.get(best) ?? []
    if (list.length >= IMAGE_FIELD_MAX_URLS || list.some((p) => p.url === url)) continue
    list.push({ url, caption })
    assigned.set(best, list)
  }

  // Room order, not first-hit order, so consumers can rely on it.
  return new Map([...assigned.entries()].sort((a, b) => a[0] - b[0]))
}
